import { z } from "zod";

const json = z.record(z.unknown());
const nullableString = z.string().nullable();

export const CardStatus = z.enum(["PENDING", "APPROVED", "NO_PRODUCT", "REJECTED"]);
export type CardStatus = z.infer<typeof CardStatus>;

export const JobStatus = z.enum(["QUEUED", "RUNNING", "DONE", "FAILED"]);
export type JobStatus = z.infer<typeof JobStatus>;

export const Verdict = z.enum([
  "correct",
  "wrong_pick",
  "not_in_candidates",
  "no_products",
  "degraded",
  "searched",
  "unscored",
]);
export type Verdict = z.infer<typeof Verdict>;

export const Basis = z.enum(["truth", "production"]).nullable();
export type Basis = z.infer<typeof Basis>;

export const Prices = z.object({
  raw: z.number().optional(),
  psa9: z.number().optional(),
  psa10: z.number().optional(),
});
export type Prices = z.infer<typeof Prices>;
export const ProductRef = z.object({
  id: z.string(),
  name: z.string(),
  console: z.string(),
  prices: Prices.optional(),
});
export type ProductRef = z.infer<typeof ProductRef>;

export const Images = z.record(nullableString);
export type Images = z.infer<typeof Images>;

export const Card = z.object({
  id: z.number(),
  scanId: z.string(),
  scanUrl: z.string(),
  scannedAt: z.string(),
  sourceImageUrl: z.string(),
  photoKey: z.string(),
  photoUrl: z.string(),
  productionProduct: ProductRef,
  status: CardStatus,
  productId: nullableString,
  identity: json.nullable(),
  sport: nullableString,
  cardFullName: nullableString,
  note: nullableString,
  reviewedBy: nullableString,
  reviewedAt: nullableString,
  createdAt: z.string(),
});
export type Card = z.infer<typeof Card>;

export const LatestResult = z.object({
  runId: z.number(),
  pickedId: nullableString,
  pickedName: nullableString,
  verdict: Verdict,
});
export type LatestResult = z.infer<typeof LatestResult>;

export const CardListItem = Card.extend({ latest: LatestResult.nullable() });
export type CardListItem = z.infer<typeof CardListItem>;

export const CardList = z.object({
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
  items: z.array(CardListItem),
});
export type CardList = z.infer<typeof CardList>;

export const FieldDiff = z.object({ field: z.string(), want: z.unknown(), got: z.unknown() });
export type FieldDiff = z.infer<typeof FieldDiff>;

export const Scored = z.object({
  verdict: Verdict,
  // whether the truth is the pick or a runner-up; null when unscored
  top3: z.boolean().nullable().default(null),
  basis: Basis,
  diffs: z.array(FieldDiff),
  sportWrong: z.boolean(),
});
export type Scored = z.infer<typeof Scored>;

export const EbayListing = z.object({
  title: z.string().optional(),
  price: z.object({ value: z.string().optional(), currency: z.string().optional() }).optional(),
  condition: z.string().optional(),
  link: z.string().optional(),
  image: z.string().optional(),
  epid: z.string().optional(),
});
export type EbayListing = z.infer<typeof EbayListing>;

export const RankedRef = z.object({
  id: z.string(),
  confidence: z.number().nullable(),
  listingVotes: z.number(),
});
export type RankedRef = z.infer<typeof RankedRef>;

const runCardBase = z.object({
  id: z.number(),
  runId: z.number(),
  cardId: z.number(),
  cardInfo: json.nullable(),
  identity: json.nullable(),
  ebayListings: z.number(),
  ebayDegraded: nullableString,
  ebayItems: z.array(EbayListing),
  products: z.array(ProductRef),
  pickedId: nullableString,
  pickedName: nullableString,
  // the pick first, then its runners-up; [] on runs before the ranking was kept
  ranked: z.array(RankedRef).default([]),
  pricing: z.enum(["priced", "no_match", "degraded"]),
  error: z.object({ stage: z.string(), message: z.string() }).nullable(),
  ms: z.object({ listings: z.number(), identify: z.number(), query: z.number(), pick: z.number() }).partial(),
  scored: Scored,
});

export const RunCardRow = runCardBase.extend({ ranAt: z.string() });
export type RunCardRow = z.infer<typeof RunCardRow>;

export const CardDetail = Card.extend({
  productionCardInfo: json,
  productionIdentity: json.nullable(),
  runs: z.array(RunCardRow),
  images: Images,
  priceQuery: z.object({ scp: z.string(), pricecharting: z.string() }),
  ebaySearch: z.string(),
});
export type CardDetail = z.infer<typeof CardDetail>;

export const Fetch = z.object({
  id: z.number(),
  status: JobStatus,
  params: z.object({
    count: z.number(),
    minPage: z.number(),
    maxPage: z.number(),
    perPage: z.number(),
    seed: z.number().nullish(),
  }),
  added: z.number(),
  pagesSeen: z.number(),
  log: z.string(),
  error: nullableString,
  runId: z.number().nullish(),
  createdBy: z.string(),
  createdAt: z.string(),
  finishedAt: nullableString,
});
export type Fetch = z.infer<typeof Fetch>;

export const Summary = z.object({
  total: z.number(),
  scored: z.number(),
  correct: z.number(),
  verified: z.number(),
  verifiedCorrect: z.number(),
  // runs summarized before top-3 scoring have none
  top3: z.number().optional(),
  verifiedTop3: z.number().optional(),
  verdicts: z.object({
    correct: z.number(),
    wrong_pick: z.number(),
    not_in_candidates: z.number(),
    no_products: z.number(),
    degraded: z.number(),
    // runs scored before web-search picks were a verdict have none
    searched: z.number().default(0),
    unscored: z.number(),
  }),
  byType: z.record(z.object({ scored: z.number(), correct: z.number() })),
  fields: z.record(z.object({ total: z.number(), ok: z.number() })),
  ebayListings: z.number(),
  cappedQueries: z.number(),
});
export type Summary = z.infer<typeof Summary>;

export const PRICE_PROMPT_TYPES = ["sports", "pokemon", "mtg", "yugioh"] as const;
export type PricePromptType = (typeof PRICE_PROMPT_TYPES)[number];

export const EngineInfo = z.object({
  scanCore: z.string(),
  identify: z.string(),
  pricePicker: z.string(),
  // runs recorded before prompts were part of the record have none
  prompts: z.object({ identify: z.string(), price: z.record(z.string()) }).optional(),
});
export type EngineInfo = z.infer<typeof EngineInfo>;

export interface EngineOverrides {
  models?: { identify?: string; pricePicker?: string };
  prompts?: { identify?: string; price?: Partial<Record<PricePromptType, string>> };
}

export const Run = z.object({
  id: z.number(),
  status: JobStatus,
  params: z.object({
    statuses: z.array(CardStatus),
    cardIds: z.array(z.number()).optional(),
    limit: z.number().optional(),
    fetchId: z.number().optional(),
    // a run done elsewhere (scan-core's dataset runner) and submitted here
    source: z.literal("report").optional(),
    label: z.string().optional(),
  }),
  engine: EngineInfo,
  summary: Summary.nullable(),
  total: z.number(),
  done: z.number(),
  error: nullableString,
  createdBy: z.string(),
  // submitted with a personal token: only its owner is served it
  private: z.boolean(),
  createdAt: z.string(),
  startedAt: nullableString,
  finishedAt: nullableString,
});
export type Run = z.infer<typeof Run>;

export const Truth = z.object({
  basis: Basis,
  productId: nullableString,
  identity: json.nullable(),
  sport: nullableString,
});
export type Truth = z.infer<typeof Truth>;

export const RunDetailCard = runCardBase.extend({ card: Card, truth: Truth });
export type RunDetailCard = z.infer<typeof RunDetailCard>;

export const RunDetail = Run.extend({ cards: z.array(RunDetailCard), images: Images });
export type RunDetail = z.infer<typeof RunDetail>;

export const Me = z.object({
  user: z.object({ email: z.string() }).nullable(),
  devLogin: z.boolean(),
});
export type Me = z.infer<typeof Me>;

export const Invited = z.object({ email: z.string() });

export const ApiToken = z.object({
  id: z.number(),
  name: z.string(),
  prefix: z.string(),
  createdAt: z.string(),
  lastUsedAt: nullableString,
});
export type ApiToken = z.infer<typeof ApiToken>;
/** The secret is in this response only; afterwards the list shows the prefix. */
export const NewToken = z.object({ token: ApiToken, secret: z.string() });
export type NewToken = z.infer<typeof NewToken>;
export const InviteLink = z.object({ link: z.string() });
export const Ok = z.object({ ok: z.boolean() });

export const SearchResult = z.object({ products: z.array(ProductRef), images: Images });
export type SearchResult = z.infer<typeof SearchResult>;

export type CatalogSource = "scp" | "pricecharting";
